"use client";

import { useState, useRef, useEffect } from "react";
import {
  Container,
  Title,
  Text,
  Stack,
  Button,
  Group,
  Paper,
  rem,
  Box,
  Flex,
  ActionIcon,
  SimpleGrid,
  Image,
  Loader,
  Modal,
  Divider,
  Select,
  SegmentedControl,
} from "@mantine/core";
import {
  IconUpload,
  IconX,
  IconCloudUpload,
  IconDownload,
  IconCamera,
  IconCheck,
  IconArrowRight,
  IconPlus,
} from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useRouter, useSearchParams } from "next/navigation";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import firebase_app from "@/firebase/config";
import { doc, getFirestore, setDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes } from "firebase/storage";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import { useDisclosure, useClickOutside, useMediaQuery } from "@mantine/hooks";
import { getApp } from "firebase/app";

// Fire-and-forget client error logger. Sends to /api/log-client-error so
// errors appear in Vercel logs (browser console alone is invisible to us).
function logClientError(
  phase: string,
  error: unknown,
  sessionId: string,
  initialPhoto: string | null,
  extraPhotoCount: number,
) {
  try {
    const payload = {
      source: "kibbe_form_submit",
      phase,
      sessionId,
      message: error instanceof Error ? error.message : String(error),
      name: error instanceof Error ? error.name : null,
      stack: error instanceof Error ? error.stack?.slice(0, 2000) : null,
      hasInitialPhoto: !!initialPhoto,
      extraPhotoCount,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      url: typeof window !== "undefined" ? window.location.href : null,
      timestamp: new Date().toISOString(),
    };
    fetch("/api/log-client-error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {}
}

// Helper to convert data URL to File
function dataURLtoFile(dataUrl: string, filename: string): File {
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)?.[1] || "image/jpeg";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

interface KibbeFormProps {
  selectedTier: string | null;
  requiredPhotos: number;
  maxPhotos?: number;
  onComplete: () => void;
  sessionId: string;
  isSubmitDisabled?: boolean;
}

// Simplified height options - common heights only
const HEIGHTS_IMPERIAL = [
  "4'10\"", "4'11\"",
  "5'0\"", "5'1\"", "5'2\"", "5'3\"", "5'4\"", "5'5\"", "5'6\"", "5'7\"", "5'8\"", "5'9\"", "5'10\"", "5'11\"",
  "6'0\"", "6'1\"", "6'2\"", "6'3\"", "6'4\"", "6'5\"",
].map(h => ({ value: h, label: h }));

const HEIGHTS_METRIC = [
  "147", "150", "152", "155", "157", "160", "163", "165", "168", "170",
  "173", "175", "178", "180", "183", "185", "188", "191", "193", "196",
].map(h => ({ value: h, label: `${h} cm` }));

export default function KibbeForm({
  selectedTier,
  requiredPhotos,
  maxPhotos = 5,
  onComplete,
  sessionId,
  isSubmitDisabled = false,
}: KibbeFormProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const includeAnalysis = searchParams.get("include_analysis") === "true";
  const isMobile = useMediaQuery("(max-width: 48em)");

  // Multi-step state
  const [step, setStep] = useState<1 | 2>(1);

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [maxPhotosError, setMaxPhotosError] = useState<string | null>(null);
  const [showLoading, setShowLoading] = useState(false);
  const [heightUnit, setHeightUnit] = useState<"imperial" | "metric">("imperial");

  // Camera modal
  const [cameraOpened, { open: openCamera, close: closeCamera }] = useDisclosure(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Add photo menu
  const [addPhotoMenuOpened, { open: openAddPhotoMenu, close: closeAddPhotoMenu }] = useDisclosure(false);
  const addPhotoMenuRef = useClickOutside(() => closeAddPhotoMenu());
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Prevent duplicate submissions
  const analysisSubmitted = useRef(false);

  // Initial photo from preview flow
  const [initialPhoto, setInitialPhoto] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      photos: [] as File[],
      height: "",
      gender: "",
    },
    validate: {
      height: (value: string) => (value ? null : "Please select your height"),
      gender: (value: string) => (value ? null : "Please select your gender"),
    },
  });

  // Load initial photo from sessionStorage on mount
  useEffect(() => {
    const savedImage = sessionStorage.getItem("kibbeAnalysisImage");
    if (savedImage) {
      setInitialPhoto(savedImage);
    }
  }, []);

  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (err) {
      notifications.show({
        title: "Camera Error",
        message: "Unable to access your camera. Please check permissions.",
        color: "red",
      });
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const takePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext("2d");
      if (context) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], `selfie-${Date.now()}.png`, { type: "image/png" });
            const newPhotos = [...form.values.photos, file];
            if (newPhotos.length > maxPhotos) {
              setMaxPhotosError(`Maximum ${maxPhotos} photos allowed.`);
            } else {
              form.setFieldValue("photos", newPhotos);
              setMaxPhotosError(null);
              closeCamera();
              stopCamera();
            }
          }
        }, "image/png");
      }
    }
  };

  const handleOpenCamera = () => {
    closeAddPhotoMenu();
    openCamera();
    setTimeout(() => startCamera(), 100);
  };

  const handleCloseCamera = () => {
    stopCamera();
    closeCamera();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;
    const newFiles = [...form.values.photos, ...Array.from(files)];
    if (newFiles.length > maxPhotos) {
      setMaxPhotosError(`Maximum ${maxPhotos} photos allowed.`);
    } else {
      form.setFieldValue("photos", newFiles);
      setMaxPhotosError(null);
    }
    event.target.value = "";
    closeAddPhotoMenu();
  };

  const openFilePicker = () => {
    closeAddPhotoMenu();
    fileInputRef.current?.click();
  };

  // Step 1 -> Step 2
  const handleContinueToPhotos = () => {
    const heightError = form.validateField("height");
    const genderError = form.validateField("gender");
    if (!heightError.hasError && !genderError.hasError) {
      setStep(2);
    }
  };

  // Final submission
  const handleSubmit = async () => {
    if (analysisSubmitted.current) return;

    let phase = "init";
    setIsSubmitting(true);
    try {
      const storage = getStorage(firebase_app);
      const firestore = getFirestore(firebase_app);

      // Upload photos
      let photoIndex = 0;
      const uploadPromises: Promise<any>[] = [];

      // Upload initial photo from sessionStorage if it exists
      if (initialPhoto) {
        phase = "fetch_initial_photo";
        const response = await fetch(initialPhoto);
        const blob = await response.blob();
        const imageRef = ref(storage, `generatedKibbeAnalyses/${sessionId}/image_${photoIndex + 1}.png`);
        uploadPromises.push(uploadBytes(imageRef, blob));
        photoIndex++;
      }

      // Upload any additional photos
      form.values.photos.forEach((file) => {
        const imageRef = ref(storage, `generatedKibbeAnalyses/${sessionId}/image_${photoIndex + 1}.png`);
        uploadPromises.push(uploadBytes(imageRef, file));
        photoIndex++;
      });

      phase = "storage_upload";
      await Promise.all(uploadPromises);

      // Merge so we don't clobber customerEmail written by verifyPayment after checkout
      phase = "firestore_write";
      const analysisRef = doc(firestore, "generatedKibbeAnalyses", sessionId);
      await setDoc(
        analysisRef,
        {
          analysisId: sessionId,
          selectedTier: selectedTier,
          createdAt: new Date(),
          status: "IN_PROGRESS",
          formData: {
            height: form.values.height,
            gender: form.values.gender,
          },
          includeAnalysis,
        },
        { merge: true }
      );

      analysisSubmitted.current = true;
      setShowLoading(true);

      // Call cloud function
      phase = "cloud_function_call";
      const functions = getFunctions(getApp());
      if (process.env.NODE_ENV !== "production") {
        connectFunctionsEmulator(functions, "localhost", 5001);
      }
      const kibbeFunction = httpsCallable(functions, "kibbeAnalysisFunction");
      kibbeFunction({ analysisId: sessionId, includeAnalysis }).catch((err) => {
        logClientError("cloud_function_call_async", err, sessionId, initialPhoto, form.values.photos.length);
      });

      setTimeout(() => {
        if (onComplete) onComplete();
      }, 1000);
    } catch (error) {
      logClientError(phase, error, sessionId, initialPhoto, form.values.photos.length);
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Something went wrong. Please try again.",
        color: "red",
      });
      analysisSubmitted.current = false;
      setShowLoading(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (showLoading) {
    return (
      <Container fluid px={0}>
        <Paper shadow="xs" p="xl" radius="md" withBorder>
          <Flex direction="column" align="center" justify="center" mih="40vh" gap="xl">
            <Loader size="xl" color="violet" />
            <Stack align="center" gap="xs">
              <Text fz="xl" fw={500}>Preparing your analysis...</Text>
              <Text fz="sm" c="dimmed" ta="center" maw={400}>
                We're analyzing your body type. This takes 1-2 minutes.
              </Text>
            </Stack>
          </Flex>
        </Paper>
      </Container>
    );
  }

  // Total photo count for display
  const totalPhotos = (initialPhoto ? 1 : 0) + form.values.photos.length;

  return (
    <Paper shadow="sm" p={{ base: "md", md: "xl" }} radius="md" withBorder bg="gray.0">
      {/* ============================================ */}
      {/* STEP 1: Height & Gender */}
      {/* ============================================ */}
      {step === 1 && (
        <Stack gap="lg">
          <Box>
            <Text fz="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>Step 1 of 2</Text>
            <Title order={2} size="h3">Quick Details</Title>
            <Text fz="sm" c="dimmed" mt={4}>
              Height is important for determining your vertical line.
            </Text>
          </Box>

          {/* Height */}
          <Box>
            <Text fz="sm" fw={500} mb={8}>Your Height</Text>
            <Group gap="xs" grow>
              <Select
                placeholder="Select height"
                data={heightUnit === "imperial" ? HEIGHTS_IMPERIAL : HEIGHTS_METRIC}
                searchable
                {...form.getInputProps("height")}
                size="md"
              />
              <SegmentedControl
                value={heightUnit}
                onChange={(value) => {
                  setHeightUnit(value as "imperial" | "metric");
                  form.setFieldValue("height", ""); // Reset on unit change
                }}
                data={[
                  { value: "imperial", label: "ft/in" },
                  { value: "metric", label: "cm" },
                ]}
                size="md"
                style={{ maxWidth: 120 }}
              />
            </Group>
            {form.errors.height && (
              <Text fz="xs" c="red" mt={4}>{form.errors.height}</Text>
            )}
          </Box>

          {/* Gender */}
          <Box>
            <Text fz="sm" fw={500} mb={8}>Gender</Text>
            <SegmentedControl
              fullWidth
              value={form.values.gender}
              onChange={(value) => form.setFieldValue("gender", value)}
              data={[
                { value: "female", label: "Female" },
                { value: "male", label: "Male" },
              ]}
              size="md"
            />
            {form.errors.gender && (
              <Text fz="xs" c="red" mt={4}>{form.errors.gender}</Text>
            )}
          </Box>

          <Button
            size="lg"
            color="violet"
            rightSection={<IconArrowRight size={18} />}
            onClick={handleContinueToPhotos}
            fullWidth
            mt="md"
          >
            Continue
          </Button>
        </Stack>
      )}

      {/* ============================================ */}
      {/* STEP 2: Photos */}
      {/* ============================================ */}
      {step === 2 && (
        <Stack gap="lg">
          <Box>
            <Text fz="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>Step 2 of 2</Text>
            <Title order={2} size="h3">Your Photos</Title>
          </Box>

          {/* Initial photo from preview */}
          {initialPhoto && (
            <Group gap="sm" align="center">
              <Box
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 8,
                  overflow: "hidden",
                  border: "2px solid #8B5CF6",
                }}
              >
                <img
                  src={initialPhoto}
                  alt="Initial photo"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
              <Box>
                <Group gap={4}>
                  <IconCheck size={14} color="#8B5CF6" />
                  <Text fz="sm" fw={500} style={{ color: "#5B21B6" }}>Photo ready</Text>
                </Group>
                <Text fz="xs" c="dimmed">From your preview</Text>
              </Box>
            </Group>
          )}

          {/* Add more photos section */}
          <Box>
            <Text fz="sm" fw={500} mb={4}>Add more photos (optional)</Text>
            <Text fz="xs" c="dimmed" mb="sm">
              One clear full-body photo gives a starting estimate. More photos can help but aren't necessary.
            </Text>

            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />

            <Box pos="relative">
              <Button
                variant="outline"
                color="violet"
                leftSection={<IconPlus size={16} />}
                onClick={openAddPhotoMenu}
                disabled={totalPhotos >= maxPhotos}
                fullWidth
                size="sm"
              >
                Add Another Photo
              </Button>

              {addPhotoMenuOpened && (
                <Paper
                  ref={addPhotoMenuRef}
                  shadow="md"
                  p="xs"
                  radius="md"
                  withBorder
                  pos="absolute"
                  top="100%"
                  left={0}
                  right={0}
                  mt={4}
                  style={{ zIndex: 100 }}
                >
                  <Stack gap={4}>
                    {!isMobile && (
                      <Button
                        variant="subtle"
                        color="gray"
                        leftSection={<IconCamera size={16} />}
                        onClick={handleOpenCamera}
                        fullWidth
                        justify="flex-start"
                        size="sm"
                      >
                        Take a photo
                      </Button>
                    )}
                    <Button
                      variant="subtle"
                      color="gray"
                      leftSection={<IconUpload size={16} />}
                      onClick={openFilePicker}
                      fullWidth
                      justify="flex-start"
                      size="sm"
                    >
                      Upload from device
                    </Button>
                  </Stack>
                </Paper>
              )}
            </Box>

            {maxPhotosError && (
              <Text c="red" fz="xs" mt={4}>{maxPhotosError}</Text>
            )}

            {/* Show additional photos */}
            {form.values.photos.length > 0 && (
              <SimpleGrid cols={{ base: 4, sm: 5 }} spacing="sm" mt="sm">
                {form.values.photos.map((file, index) => (
                  <Box key={index} pos="relative" style={{ maxWidth: 64 }}>
                    <Image
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      radius="md"
                      fit="cover"
                      h={56}
                      w="100%"
                      style={{ maxWidth: 64, maxHeight: 56, objectFit: "cover" }}
                    />
                    <ActionIcon
                      variant="filled"
                      pos="absolute"
                      top={rem(-6)}
                      right={rem(-6)}
                      color="gray"
                      size="xs"
                      radius="xl"
                      onClick={() => {
                        const newPhotos = [...form.values.photos];
                        newPhotos.splice(index, 1);
                        form.setFieldValue("photos", newPhotos);
                        setMaxPhotosError(null);
                      }}
                    >
                      <IconX style={{ width: rem(10), height: rem(10) }} />
                    </ActionIcon>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </Box>

          {/* Summary */}
          <Paper p="md" radius="md" withBorder bg="white">
            <Text fz="xs" fw={600} tt="uppercase" mb="sm" style={{ color: "#94A3B8", letterSpacing: 0.5 }}>
              Your analysis will include:
            </Text>
            <Stack gap={4}>
              {[
                "An explained Kibbe type estimate",
                "Yin/Yang balance breakdown",
                "Personalized silhouette guide",
                "Necklines, fabrics & patterns",
                "What to wear & what to avoid",
              ].map((item, i) => (
                <Group key={i} gap={6} wrap="nowrap">
                  <IconCheck size={14} color="#8B5CF6" />
                  <Text fz="xs" style={{ color: "#374151" }}>{item}</Text>
                </Group>
              ))}
            </Stack>
          </Paper>

          {/* Actions */}
          <Group grow>
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setStep(1)}
            >
              Back
            </Button>
            <Button
              size="lg"
              color="violet"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitDisabled || totalPhotos < 1}
            >
              Run Analysis
            </Button>
          </Group>
        </Stack>
      )}

      {/* Camera Modal */}
      <Modal opened={cameraOpened} onClose={handleCloseCamera} title="Take a Photo" centered size="md">
        <Stack>
          <Box h={350} style={{ overflow: "hidden", borderRadius: 8 }}>
            <video ref={videoRef} autoPlay playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </Box>
          <Button onClick={takePhoto} leftSection={<IconCamera size={18} />} color="violet">
            Capture Photo
          </Button>
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </Stack>
      </Modal>
    </Paper>
  );
}
